import * as z from "zod";
import {
  createOrderEditInputSchema,
  createOrderEditOutputSchema,
} from "./base";

/**
 * Schema for updating order items (add, remove, or change quantity)
 */

/**
 * Discount to apply to a line item (only valid for "add" action).
 */
const OrderOperationDiscount_ = z.object({
  /** Percentage discount (0-100). */
  percentValue: z.number().min(0).max(100).optional(),
  /** Fixed discount amount as a string (e.g., "10.00"). */
  fixedValue: z.string().optional(),
  /**
   * Currency code for fixedValue (e.g., "USD", "EUR").
   * TODO: This should be required when fixedValue is used - callers should always pass the order's currency.
   */
  currencyCode: z.string().optional(),
  /** Description shown on the order. */
  description: z.string(),
});

export const OrderOperationDiscountSchema: z.ZodType<OrderOperationDiscount> =
  OrderOperationDiscount_;
export interface OrderOperationDiscount extends z.infer<
  typeof OrderOperationDiscount_
> {}

// Individual operation type that can be reused
const OrderOperation_ = z.object({
  variantId: z.string().describe("The variant ID to add or remove"),
  quantity: z.number().positive().describe("The quantity to add or remove"),
  action: z.enum(["add", "remove"]).describe("The action to perform"),
  /** Optional discount to apply (only valid for "add" action) */
  discount: OrderOperationDiscountSchema.optional(),
});

export const OrderOperationSchema: z.ZodType<OrderOperation> = OrderOperation_;
export interface OrderOperation extends z.infer<typeof OrderOperation_> {}

// Data schema for order items update
const UpdateOrderItemsData_ = z.object({
  operations: z
    .array(OrderOperationSchema)
    .describe("Array of operations to perform on the order"),
});

export const UpdateOrderItemsDataSchema: z.ZodType<UpdateOrderItemsData> =
  UpdateOrderItemsData_;
export interface UpdateOrderItemsData extends z.infer<
  typeof UpdateOrderItemsData_
> {}

// Full input schema
const UpdateOrderItemsInput_ = createOrderEditInputSchema(
  UpdateOrderItemsDataSchema,
);

export const UpdateOrderItemsInput: z.ZodType<UpdateOrderItemsInput> =
  UpdateOrderItemsInput_;
export interface UpdateOrderItemsInput extends z.infer<
  typeof UpdateOrderItemsInput_
> {}

// Output data schema - the result-specific fields
const UpdateOrderItemsOutputData_ = z.array(
  z.object({
    variantId: z.string(),
    action: z.enum(["add", "remove", "update"]),
    oldTotal: z.number(),
    newTotal: z.number(),
    quantity: z.number(),
    lineItemId: z.string().optional(),
    lineItemName: z.string().optional(),
    productTitle: z.string().optional(),
    variantTitle: z.string().optional(),
    sku: z.string().optional(),
  }),
);

export const UpdateOrderItemsOutputDataSchema: z.ZodType<UpdateOrderItemsOutputData> =
  UpdateOrderItemsOutputData_;
export interface UpdateOrderItemsOutputData extends z.infer<
  typeof UpdateOrderItemsOutputData_
> {}

// Full output schema
const UpdateOrderItemsOutput_ = createOrderEditOutputSchema(
  UpdateOrderItemsOutputDataSchema,
);

export const UpdateOrderItemsOutput: z.ZodType<UpdateOrderItemsOutput> =
  UpdateOrderItemsOutput_;
export interface UpdateOrderItemsOutput extends z.infer<
  typeof UpdateOrderItemsOutput_
> {}
